import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  ListItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useSelector } from "react-redux";
import { usePresignedUpload } from "src/hooks/usePresignedUpload";
import { MENU_IMAGE } from "src/utils/uploadCategories";
import { BagIcon } from "src/@core/components/icons/BagIcon";
import { Delete } from "mdi-material-ui";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function MenuCreateDialog({
  editMenu,
  selectedCategories,
  setSelectedCategories,
  onClose,
  onMenuCreated,
  onAddCategoryClick,
  onEditCategory,
  isCatalog,
}) {
  const { t } = useTranslation("dashboard");
  const [menuName, setMenuName] = useState("");
  const [locationIds, setLocationIds] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});

  const selectedBusinessId = useSelector(
    (state) => state.auth.selectedBusinessId
  );
  const queryClient = useQueryClient();

  // Set default data when editMenu changes
  useEffect(() => {
    if (editMenu) {
      setMenuName(editMenu.name || "");
      
      // Extract location IDs from location objects
      const locationIds = editMenu.locationIds?.map(location => 
        typeof location === 'string' ? location : location._id
      ) || [];
      setLocationIds(locationIds);
      
      // Set selected categories from editMenu.categories
      if (editMenu.categories && Array.isArray(editMenu.categories)) {
        const categoriesData = editMenu.categories.map(cat => ({
          id: cat._id,
          name: cat.name,
          image: cat.image,
          menuItemIds: cat.menuItemIds || []
        }));
        setSelectedCategories(categoriesData);
      } else {
        setSelectedCategories([]);
      }
      
      setImagePreview(editMenu.image || "");
    } else {
      resetForm();
    }
  }, [editMenu, setSelectedCategories]);

  // Business location data query
  const { data: businessLocationData } = useQuery(
    ["businessLocationData", selectedBusinessId],
    async ({ signal }) => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.BUSINESS_LOCATION.getAll}/${selectedBusinessId}`,
        {
          signal,
        }
      );
      return res.data.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 30 * 60 * 1000,
    }
  );

  const { mutateAsync: uploadFileWithPresignedUrl } = usePresignedUpload();

  // Menu creation mutation
  const createMenuMutation = useMutation(
    async (menuData) => {
      const response = await axiosInstance.post(
        `${ApiEndPoints.MENU.add}/${selectedBusinessId}`,
        menuData
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Refetch menus list in parent component
        queryClient.invalidateQueries(["menus", selectedBusinessId, isCatalog]);
        // Reset form
        resetForm();
        // Call success callback
        onMenuCreated?.(data);
        onClose();
      },
      onError: (error) => {
        console.error("Error creating menu:", error);
        setErrors({ submit: t("menuCreateFailedCreate") });
      },
    }
  );

  // Menu update mutation
  const updateMenuMutation = useMutation(
    async (menuData) => {
      const response = await axiosInstance.patch(
        `${ApiEndPoints.MENU.add}/${editMenu._id}`,
        menuData
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Refetch menus list in parent component
        queryClient.invalidateQueries(["menus", selectedBusinessId, isCatalog]);
        // Reset form
        resetForm();
        // Call success callback
        onMenuCreated?.(data);
        onClose();
      },
      onError: (error) => {
        console.error("Error updating menu:", error);
        setErrors({ submit: t("menuCreateFailedUpdate") });
      },
    }
  );

  const validateForm = () => {
    const newErrors = {};

    if (!menuName.trim()) {
      newErrors.menuName = t("menuCreateMenuNameRequired");
    }

    if (locationIds.length === 0) {
      newErrors.locationIds = t("menuCreateAtLeastOneLocation");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setMenuName("");
    setLocationIds([]);
    setImage(null);
    setImagePreview("");
    setErrors({});
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    setImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview("");
    }
  };

  const handleLocationChange = (event) => {
    const {
      target: { value },
    } = event;
    setLocationIds(typeof value === "string" ? value.split(",") : value);
    // Clear location error when user selects locations
    if (errors.locationIds) {
      setErrors((prev) => ({ ...prev, locationIds: "" }));
    }
  };

  const processMenuPayload = async () => {
    let imageName = null;
    
    // Handle image upload/retention
    if (image) {
      // New image selected — upload via presigned URL
      const result = await uploadFileWithPresignedUrl({ file: image, category: MENU_IMAGE });
      imageName = result?.fileUrl;
    } else if (editMenu && editMenu.image) {
      // Keep existing image as-is
      imageName = editMenu.image;
    }

    // Build payload
    const menuPayload = {
      name: menuName,
      locationIds: locationIds, // This will be array of location IDs
      ...(imageName && { image: imageName }),
      isCatalog: isCatalog,
    };

    if (selectedCategories.length > 0) {
      menuPayload.categories = selectedCategories.map((cat) => cat.id);
    }

    return menuPayload;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const menuPayload = await processMenuPayload();
      console.log("menuPayload", menuPayload);

      if (editMenu) {
        // Update existing menu
        updateMenuMutation.mutate(menuPayload);
      } else {
        // Create new menu
        createMenuMutation.mutate(menuPayload);
      }
    } catch (error) {
      console.error("Error processing menu:", error);
      setErrors({ submit: t("menuCreateFailedProcess") });
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isLoading =
    createMenuMutation.isLoading ||
    updateMenuMutation.isLoading;

  // Get selected location names for display
  const getSelectedLocationNames = () => {
    if (!businessLocationData?.locations || locationIds.length === 0) return [];
    return businessLocationData.locations
      .filter((loc) => locationIds.includes(loc._id))
      .map((loc) => loc.businessLocationName);
  };

  return (
    <>
      <Box
        sx={{
          // width: "100vw",
          maxWidth: {
            xs: "100vw",
            sm: "95vw",
            md: 500,
            lg: 600,
            xl: 700,
          },
          mx: "auto",
          bgcolor: "#fff",
          borderRadius: {
            xs: "24px",
            sm: 3,
            md: 4,
          },
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          boxSizing: "border-box",
          "@media (max-width: 600px)": {
            minWidth: "100vw",
            maxWidth: "100vw",
            borderRadius: "24px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            // background: "linear-gradient(90deg, #6c00e6 0%, #6c00e6 100%)",
            color: "#8000c2",
            px: 0,
            pt: 0,
            pb: 0,
            position: "relative",
            minHeight: 54,
            borderTopLeftRadius: { xs: "24px", sm: 12 },
            borderTopRightRadius: { xs: "24px", sm: 12 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: 8,
              //   background: "linear-gradient(90deg, #6c00e6 0%, #6c00e6 100%)",
              borderTopLeftRadius: { xs: "24px", sm: 12 },
              borderTopRightRadius: { xs: "24px", sm: 12 },
            }}
          />
          <Typography
            sx={{
              pl: 3,
              pt: 1.3,
              fontWeight: 700,
              fontSize: 21,
              color: "#000",
              letterSpacing: 0.2,
              position: "relative",
              zIndex: 1,
              //   background: "#6c00e6",
              width: "fit-content",
              borderTopLeftRadius: 7,
            }}
          >
            {editMenu
              ? (isCatalog ? t("menuCreateEditCatalog") : t("menuCreateEditMenu"))
              : (isCatalog ? t("menuCreateCreateCatalog") : t("menuCreateCreateMenu"))
            }
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 13,
              color: "#c09adf",
              background: "transparent",
              "&:hover": { color: "#8000c2", background: "#f3e7ff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 27 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: { xs: 2, sm: 4 },
            pt: 2,
            pb: 3,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Error Alert */}
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          {/* Upload section */}
          <Box
            sx={{
              border: "2px dashed #BDBDBD",
              borderRadius: 3,
              minHeight: 95,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              bgcolor: "#faf6ff",
              mb: 0.5,
              justifyContent: "center",
              transition: "border .2s",
              "&:hover": { borderColor: "#8000c2" },
              cursor: "pointer",
              width: "100%",
              boxSizing: "border-box",
            }}
            component="label"
            htmlFor="menu-image-upload"
          >
            {imagePreview ? (
              <Avatar
                src={imagePreview}
                alt="menu cover"
                variant="rounded"
                sx={{
                  width: 90,
                  height: 70,
                  borderRadius: 2,
                  objectFit: "cover",
                  mt: 0.5,
                  mb: 0.5,
                }}
              />
            ) : (
              <Box sx={{ textAlign: "center", py: 1.2 }}>
                <CloudUploadOutlinedIcon
                  sx={{ color: "#ae61e3", fontSize: 30, mb: 0.5 }}
                />
                <Typography
                  sx={{
                    color: "#8000c2",
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {t("menuCreateUploadPic")}
                </Typography>
              </Box>
            )}
            <input
              id="menu-image-upload"
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />
          </Box>
          <Typography sx={{ color: "#a18fc7", fontSize: 13, mb: 2, ml: 1 }}>
            {isCatalog ? t("menuCreateCatalogCoverOptional") : t("menuCreateMenuCoverOptional")}
          </Typography>

          {/* Menu name */}
          <TextField
            label={isCatalog ? t("menuCreateCatalogName") : t("menuCreateMenuName")}
            variant="outlined"
            size="small"
            fullWidth
            required
            value={menuName}
            onChange={(e) => setMenuName(e.target.value)}
            error={!!errors.menuName}
            helperText={errors.menuName}
            sx={{
              mb: 2,
              background: "#f9f7fb",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                fontWeight: 500,
                letterSpacing: 0.1,
              },
            }}
            InputLabelProps={{
              shrink: !!menuName,
              style: { fontWeight: 400, color: "#bbb" },
            }}
          />

          {/* Multiple Location select */}
          <FormControl
            fullWidth
            size="small"
            error={!!errors.locationIds}
            sx={{
              mb: 2,
              background: "#f9f7fb",
              borderRadius: 2,
              marginTop: 2,
            }}
          >
            <InputLabel
              id="location-multiple-select-label"
              shrink={true}
              style={{ fontWeight: 400, color: "#bbb" }}
            >
              {t("menuCreateSelectLocations")}
            </InputLabel>
            <Select
              labelId="location-multiple-select-label"
              placeholder={t("menuCreateSelectLocations")}
              multiple
              value={locationIds}
              onChange={handleLocationChange}
              input={<OutlinedInput label={t("menuCreateSelectLocations")} />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {getSelectedLocationNames().map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        fontSize: 12,
                        height: 24,
                      }}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
              sx={{
                ".MuiSelect-select": {
                  color: "#6139b5",
                  opacity: 1,
                  fontWeight: 500,
                },
                "& .MuiOutlinedInput-root": {
                  fontWeight: 500,
                },
              }}
            >
              {businessLocationData?.locations?.map((location) => (
                <MenuItem key={location._id} value={location._id}>
                  <Checkbox
                    checked={locationIds.indexOf(location._id) > -1}
                    sx={{
                      color: "#6c00e6",
                      "&.Mui-checked": {
                        color: "#6c00e6",
                      },
                    }}
                  />
                  <ListItemText primary={location.businessLocationName} />
                </MenuItem>
              ))}
            </Select>
            {errors.locationIds && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 1.5, fontSize: 12 }}
              >
                {errors.locationIds}
              </Typography>
            )}
          </FormControl>

          <Divider sx={{ mb: 2 }} />

          {/* Category section */}
          <Typography
            sx={{
              color: "#a18fc7",
              fontSize: 13,
              mb: 0,
              fontWeight: 400,
              ml: 1,
            }}
          >
            {t("menuCreateCategoryAddedLater")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 1,
              mb: 1,
              ml: 1,
              width: "100%",
              boxSizing: "border-box",
            }}
            onClick={onAddCategoryClick}
          >
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "#fff0e1",
                color: "#E96E25",
                width: 23,
                height: 23,
                mr: 1,
                fontSize: 18,
                borderRadius: "23%",
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography
              sx={{
                color: "#c06e24",
                fontSize: 14.5,
                fontWeight: 500,
                mr: 1,
              }}
            >
              {t("menuCreateCategoryCount", { count: selectedCategories?.length })}
            </Typography>
            <Typography
              component="span"
              sx={{
                color: "#6c00e6",
                fontSize: 14,
                fontWeight: 500,
                ml: 1,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {t("menuCreateAddCategory")}
            </Typography>
          </Box>

          {selectedCategories?.map((cat, i) => (
            <ListItem
              key={cat.id}
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 2,
                bgcolor: "#fff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                mb: { xs: 2, sm: 2 },
                "&:last-child": { mb: 0 },
                width: "100%",
                overflow: "hidden",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flex: 1,
                }}
              >
                {/* 1. Serial Number like image */}
                <Typography
                  sx={{
                    // width: 18,
                    color: "#222",
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                >
                  {i + 1}
                </Typography>

                {/* 3. Category Image with Edit (Pen) overlay */}
                <Box sx={{ position: "relative", mr: 1 }}>
                  <Avatar
                    variant="rounded"
                    src={cat.image || undefined}
                    alt={cat.name}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: "#f3f4f6",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  />
                  {/* Pen icon overlay */}
                  {/* <Box
                    sx={{
                      position: "absolute",
                      bottom: -6,
                      right: -8,
                      background: "#fff",
                      borderRadius: "50%",
                      p: "1px",
                    }}
                    onClick={() => {
                      console.log("cat---", cat);
                      onEditCategory(cat);
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "#8000c2",
                        width: 22,
                        height: 22,
                        border: "2px solid #fff",
                        cursor: "pointer",
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14, color: "#fff" }} />
                    </Avatar>
                  </Box> */}
                </Box>

                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#222",
                    fontSize: 16,
                    minWidth: 120,
                    textTransform: "capitalize",
                  }}
                >
                  {cat.name}
                </Typography>
                {/* 5. Bag icon and items count */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: "auto",
                    gap: 1,
                  }}
                >
                  <BagIcon />
                  <Box
                    sx={{
                      ml: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#8000c2",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {t("menuCreateItemsCount", { count: cat?.menuItemIds?.length || 0 })}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#bdbdbd",
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                    >
                      {t("menuCreateAddItems")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Edit and Delete buttons */}
              <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                {/* <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log("Edit category:", cat);
                    onEditCategory(cat);
                  }}
                  sx={{
                    color: "#6c00e6",
                    "&:hover": {
                      bgcolor: "#f3e7ff",
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton> */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategories(
                      selectedCategories.filter((item) => item.id !== cat.id)
                    );
                  }}
                  sx={{
                    color: "#dc2626",
                    "&:hover": {
                      bgcolor: "#fef2f2",
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </ListItem>
          ))}

          {/* Save Button */}
          <Button
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              mt: 3,
              mb: 1,
              bgcolor: "#6c00e6",
              color: "#fff",
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 17,
              textTransform: "lowercase",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#6c00e6",
              },
              "&:disabled": {
                bgcolor: "#c0c0c0",
              },
              height: 44,
              letterSpacing: 0.3,
            }}
            onClick={handleSave}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : editMenu ? (
              t("menuCreateUpdate")
            ) : (
              t("menuCreateSave")
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
}
